import _ from 'lodash';

import AgentVoiceCapabilitiesChecker from '../configuration/lib/agent_voice_capabilities_checker';
import analytics from 'scripts/lib/analytics';
import { checkAndResetNavigatingToNext } from 'actions/conversation/lib/conversation_workflow';
import ErrorReporter from 'scripts/infrastructure/error_reporter';
import hasLiveConnection from 'actions/phone_call/lib/has_live_connection';
import PhoneCall from 'models/phone_call';
import qconsole from 'scripts/lib/qconsole';
import updateConnectionState from 'actions/health/lib/update_connection_state';
import ShowIncomingCallSystemNotification from 'actions/notification/show_incoming_call_system_notification';
import { TwilioDeviceStatus } from 'models/connection_states';

export default class PhoneGatewayObserver {
  constructor(context) {
    this.context = context;
    this.updateConnectionState = updateConnectionState.bind(this, 'twilio');
    this.agentVoiceCapabilitiesChecker = new AgentVoiceCapabilitiesChecker(this.context);
  }

  handleRegisteredAgent() {}

  handleRequestError(errorDto) {
    qconsole.log(`Phone gateway error occurred: ${JSON.stringify(errorDto)}`);
    this.handlePhoneStatus(TwilioDeviceStatus.OFFLINE);
  }

  handleAccept() {}

  handleAcceptError(message) {
    ErrorReporter.reportMessage(message, {
      extra: {
        connectionState: this.context.stores.connectionState.get(),
      },
    });
  }

  handleCall() {}

  handleCancelError(message) {
    ErrorReporter.reportMessage(message, {
      extra: {
        connectionState: this.context.stores.connectionState.get(),
      },
    });
  }

  handleConnect() {
    this.updateConnectionState(state => state.setConnected());
  }

  handleDeviceError() {
    // Re-evaluate our device status permissions.
    // This will ensure we still have microphone permissions.
    this.context.gateways.deviceStatus.getStatus();
  }

  handleDisconnect() {
    this.updateConnectionState(state => state.setDisconnected());
  }

  handleTwilioEnabled() {
    let connectionStates = this.context.stores.connectionState.get();
    connectionStates.setTwilioEnabled();
    this.context.stores.connectionState.set(connectionStates);
    let allowIncomingCallsWhileBusy = this.context.stores.appFeatures.get().isEnabled('allowCallsWhileBusy');
    this.context.gateways.phoneGateway.setAllowIncomingCallsWhileBusy(allowIncomingCallsWhileBusy);
  }

  handleTwilioDisabled() {
    let connectionStates = this.context.stores.connectionState.get();
    connectionStates.setTwilioDisabled();
    this.context.stores.connectionState.set(connectionStates);
  }

  // Track incoming call events in Amplitude
  _trackIncomingCallEvent(callId, eventName) {
    let appFeatures = this.context.stores.appFeatures.get();
    let isOfferedCallLoggingEnabled = appFeatures.isEnabled('offeredCallLogging');
    let activeCall = this.context.stores.activeCall.get();
    let pendingActiveCall = this.context.stores.activeCall.getPending();
    if (pendingActiveCall && !activeCall) {
      // Outgoing calls can have either a committed or pending activeCall in the store at this point. We just need to determine if the call is outgoing and attempt to get the convo item ID
      activeCall = pendingActiveCall;
    }
    let isOutgoing = activeCall && activeCall.conversationItem.content.isOutgoing();

    if (isOfferedCallLoggingEnabled && !isOutgoing) {
      analytics.track(eventName, {
        callId,
        conversationItemId: _.get(activeCall, 'conversationItem.id', ''),
      });
    }
  }

  handleActiveCall() {
    let currentAgent = this.context.stores.currentAgent.get();
    let callId = _.get(this.context.gateways.phoneGateway.connection, 'parameters.CallSid', '');
    let appFeatures = this.context.stores.appFeatures.get();

    // Track all incoming call events
    this._trackIncomingCallEvent(callId, 'Incoming Call Received');

    if (!this.agentVoiceCapabilitiesChecker.canHandlePhoneCalls()) {
      ErrorReporter.reportError(new Error('handleActiveCall: agent missing voice capabilities'), {
        extra: {
          agentId: currentAgent && currentAgent.id,
          callId,
          connectionState: this.context.stores.connectionState.get(),
          phonePreference: currentAgent && currentAgent.getVoiceConfigurationPreference(),
          microphonePermissions: appFeatures.isEnabled('microphone'),
        },
      });
      let preventAutoRejectCalls = appFeatures.isEnabled('preventAutoRejectCalls');
      if (preventAutoRejectCalls) {
        // This is will silently ignore (but not auto-decline) incoming call connections when the Agent is unable to take calls. This allows agents to still handle incoming calls when one of their tabs or windows of Gladly can not handle calls.
        // We don't want to auto-decline the call here since declining the call cancels it from other tabs of Gladly that can handle phone calls
        this._trackIncomingCallEvent(callId, 'Incoming Call Ignored');
        return;
      }
      this.context.gateways.phoneGateway.decline();
      this._trackIncomingCallEvent(callId, 'Incoming Call Auto-Declined');
      return;
    }

    // Need to check for both pending and committed active calls just in case the mqtt success event comes in after the event from Twilio

    // The MQTT publish success event will typically be handled first where
    // we subscribe and commit the pending active call
    let activeCall = this.context.stores.activeCall.get();
    if (activeCall) {
      let phoneCallContent = activeCall.conversationItem.content;
      if (phoneCallContent.status === PhoneCall.Status.OUTGOING) {
        this.context.gateways.phoneGateway.accept();
      } else {
        // Show green incoming call banner and system notification if there is an offered call by the time the phone connection is established
        // Delayed incoming call connection
        const navigatingToNext = checkAndResetNavigatingToNext(this.context);
        let hasLivePhoneConnection = hasLiveConnection(this.context, { activeCall });
        activeCall.setHasLiveConnection(hasLivePhoneConnection);
        this.context.stores.activeCall.set(activeCall);
        if (!navigatingToNext) {
          this.context.executeAction(ShowIncomingCallSystemNotification);
        }
      }
      this._trackIncomingCallEvent(callId, 'Incoming Call Handled');
      return;
    }

    // it is possible that the activeCall is not committed in the store if the Twilio incoming call event is handled before the MQTT publish success event
    // in this case, we need to commit the pending active call to display the active call controls
    let pendingActiveCall = this.context.stores.activeCall.getPending();
    if (pendingActiveCall) {
      let phoneCallContent = pendingActiveCall.conversationItem.content;
      if (phoneCallContent.status === PhoneCall.Status.OUTGOING) {
        this.context.stores.activeCall.commitPending();
        this.context.gateways.phoneGateway.accept();
      }
    }

    // activeCall store should always be populated by now for outgoing calls. The activeCall store may not be populated for incoming calls yet if the offered call routing event hasn't been handled yet. In case the offered call routing event is delayed or was missed, attempt to fetch the active call.
    let isFetchActiveCallWhenDialed = appFeatures.isEnabled('fetchActiveCallWhenDialed');
    if (isFetchActiveCallWhenDialed) {
      activeCall = this.context.stores.activeCall.get();
      if (!activeCall) {
        this.context.gateways.activeCall.fetch(currentAgent.id);
      }
    }

    // Track incoming call events that are not ignored or auto declined due to missing permissions or capabilities
    this._trackIncomingCallEvent(callId, 'Incoming Call Handled');
  }

  handleHangup(conversationItemId, payload) {}

  handlePhoneControlsSuccess(conversationItemId, payload) {
    this.context.stores.activeCall.commitPending();
  }

  handlePhoneControlsErrors(conversationItemId, errorDto) {
    // Temporary work around for handling disconnect errors to ensure agents can always disconnect
    // from the call. Additional phone control error handling should be added in the future
    let pendingActiveCall = this.context.stores.activeCall.getPending();
    let currentAgentId = this.context.stores.currentAgent.get().id;
    let isCurrentAgentDisconnecting =
      pendingActiveCall && pendingActiveCall.conversationItem.content.isParticipantDisconnecting(currentAgentId);
    if (isCurrentAgentDisconnecting) {
      this.context.stores.activeCall.resetPending();
      return;
    }
    this.context.stores.activeCall.commitPending();
  }

  handleCallQualityWarning(warningName) {
    this.handleCallQualityEvent(warningName, 'STARTED');
  }

  handleCallQualityWarningCleared(warningName) {
    this.handleCallQualityEvent(warningName, 'ENDED');
  }

  handleCallQualityEvent(warningName, status) {
    const activeCall = this.context.stores.activeCall.get();
    if (activeCall) {
      let currentAgentId = this.context.stores.currentAgent.get().id;
      let event = {
        eventType: warningName,
        status,
        agentId: currentAgentId,
      };

      this.context.gateways.phoneControlsHttp.recordQualityEvent(
        activeCall.customer.id,
        activeCall.conversationItem.id,
        event
      );
    }
  }

  /*
  Updates the Twilio connection status to be either ready, busy, or offline.
  - Status will be updated to 'ready' when device becomes available to take calls.
  - Status will be updated to 'busy' when device has an active call or is setup but not available.
  - Status will be updated to 'offline' when device is not setup or after a valid token expires
  */
  handlePhoneStatus(status) {
    if (_.keys(TwilioDeviceStatus).indexOf(status) === -1) {
      ErrorReporter.reportMessage(`could not handle browser phone status`, {
        extra: {
          status,
        },
      });
      return;
    }

    let connectionStates = this.context.stores.connectionState.get();
    connectionStates.updateTwilioStatus(status);
    this.context.stores.connectionState.set(connectionStates);
  }
}



// WEBPACK FOOTER //
// ./src/scripts/application/actions/phone_call/phone_gateway_observer.js